import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  calendarSelectionToSiteKey,
  formatSiteCutoffTime,
  formatSiteDateLabel,
  formatSiteTimeRange,
  siteTodayKey,
} from "@/lib/datetime.schedule";
import { dayPickerI18n } from "@/lib/day-picker-i18n";
import { localizeBookingError } from "@/lib/booking-errors";
import { useBookingWizardQueries } from "@/hooks/useBookingWizardQueries";
import {
  type BookingMode,
  type MemberPackage,
  type SitePrice,
  bookingModeOfferingLabel,
  bookingModePurchaseIds,
  existingPackageSessionInfo,
  partitionBookingOfferings,
  resolveLessonTypeBySlug,
} from "@/lib/booking-mode";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getBookingCutoff, getMyPackages, createBooking } from "@/lib/booking.functions";
import { getMyProfile } from "@/lib/profile.functions";
import { formatDualPrice, BOOKABLE_LESSON_SLUGS } from "@/lib/marketing-pricing";
import { SITE_PHONE, SITE_PHONE_TEL } from "@/lib/site-info";
import { AuthInline } from "@/components/auth/AuthInline";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Check, ChevronLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingSitePriceCard } from "@/components/booking/BookingSitePriceCard";
import { localizePriceLabel } from "@/lib/localize-price-label";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime";

type LessonType = {
  id: string;
  name: string;
  slug: string;
  sport: string;
  duration_minutes: number;
  price_cents: number;
  description: string | null;
};
type Slot = { start: string; end: string };

type ScheduleBookingWizardProps = {
  packageParam?: string;
};

export function ScheduleBookingWizard({ packageParam }: ScheduleBookingWizardProps) {
  const navigate = useNavigate();
  const locale = getLocale();
  const calendarI18n = useMemo(() => dayPickerI18n(locale), [locale]);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [bookingMode, setBookingMode] = useState<BookingMode | null>(null);
  const [lessonType, setLessonType] = useState<LessonType | null>(null);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [authed, setAuthed] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [userEmail, setUserEmail] = useState("");
  const [detailsPrefilled, setDetailsPrefilled] = useState(false);
  const [details, setDetails] = useState({ studentName: "", studentAge: "", phone: "", notes: "" });

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setAuthed(!!data.session);
      setUserEmail(data.session?.user?.email ?? "");
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_e, s) => {
      setAuthed(!!s);
      setUserEmail(s?.user?.email ?? "");
      if (!s) {
        setDetailsPrefilled(false);
        setDetails({ studentName: "", studentAge: "", phone: "", notes: "" });
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const cutoffQ = useQuery({ queryKey: ["booking-cutoff"], queryFn: () => getBookingCutoff() });
  const myPackagesQ = useQuery({
    queryKey: ["my-packages"],
    queryFn: () => getMyPackages(),
    enabled: authed,
  });
  const profileQ = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => getMyProfile(),
    enabled: authed,
  });

  useEffect(() => {
    if (!authed || !profileQ.data || detailsPrefilled) return;
    setDetails((prev) => ({
      studentName: profileQ.data.full_name ?? prev.studentName,
      studentAge: prev.studentAge,
      phone: profileQ.data.phone ?? prev.phone,
      notes: prev.notes,
    }));
    if (profileQ.data.email) setUserEmail(profileQ.data.email);
    setDetailsPrefilled(true);
  }, [authed, profileQ.data, detailsPrefilled]);

  const { lessonTypesQ, sitePricesQ, bookableDatesQ, slotsQ } = useBookingWizardQueries({
    lessonTypeId: lessonType?.id,
    date,
    calendarMonth,
    skipCutoff: false,
    bookableEnabled: !!lessonType && step >= 2,
    slotsEnabled: !!date && !!lessonType,
  });

  const bookableSet = useMemo(() => new Set(bookableDatesQ.data ?? []), [bookableDatesQ.data]);

  const lessonTypes = useMemo(() => lessonTypesQ.data ?? [], [lessonTypesQ.data]);
  const sitePrices = sitePricesQ.data ?? [];
  const { singlePrices, packagePrices, specialtyLessons } = partitionBookingOfferings(
    lessonTypes,
    sitePrices,
    BOOKABLE_LESSON_SLUGS,
  );
  const myPackages = useMemo(() => myPackagesQ.data ?? [], [myPackagesQ.data]);
  const offeringsLoading = lessonTypesQ.isLoading || sitePricesQ.isLoading;

  const { memberPackageId, sitePriceId } = bookingModePurchaseIds(bookingMode);

  const offeringLabel = useMemo(
    () => bookingModeOfferingLabel(bookingMode, lessonType?.name),
    [bookingMode, lessonType],
  );

  useEffect(() => {
    if (!packageParam || !authed || !myPackages.length || !lessonTypes.length || bookingMode)
      return;
    const pkg = myPackages.find((p) => p.id === packageParam);
    if (!pkg) return;
    const lt = resolveLessonTypeBySlug(lessonTypes, pkg.lesson_type_slug);
    if (!lt) return;
    setBookingMode({ kind: "existing-package", memberPackage: pkg });
    setLessonType(lt);
    setStep(2);
  }, [packageParam, authed, myPackages, lessonTypes, bookingMode]);

  const bookMut = useMutation({
    mutationFn: () =>
      createBooking({
        data: {
          lessonTypeId: lessonType!.id,
          startAt: slot!.start,
          studentName: details.studentName,
          studentAge: details.studentAge ? Number(details.studentAge) : null,
          phone: details.phone,
          notes: details.notes.trim() || null,
          memberPackageId,
          sitePriceId,
        },
      }),
    onSuccess: () => {
      toast.success(m.booking_toast_submitted());
      navigate({ to: "/my-bookings" });
    },
    onError: (e: Error) =>
      toast.error(localizeBookingError(e.message, locale, cutoffQ.data?.cutoffTime)),
  });

  const selectSitePrice = (sitePrice: SitePrice, kind: "single" | "new-package") => {
    const lt = resolveLessonTypeBySlug(lessonTypes, sitePrice.lesson_type_slug);
    if (!lt) {
      toast.error(m.booking_toast_type_unavailable());
      return;
    }
    setBookingMode({ kind, sitePrice });
    setLessonType(lt);
    setDate(undefined);
    setSlot(null);
    setStep(2);
  };

  const selectExistingPackage = (memberPackage: MemberPackage) => {
    const lt = resolveLessonTypeBySlug(lessonTypes, memberPackage.lesson_type_slug);
    if (!lt) {
      toast.error(m.booking_toast_type_unavailable());
      return;
    }
    setBookingMode({ kind: "existing-package", memberPackage });
    setLessonType(lt);
    setDate(undefined);
    setSlot(null);
    setStep(2);
  };

  const selectSpecialty = (lt: LessonType) => {
    setBookingMode({ kind: "specialty" });
    setLessonType(lt);
    setDate(undefined);
    setSlot(null);
    setStep(2);
  };

  const handleDateSelect = (d: Date | undefined) => {
    if (!d) return;
    const key = calendarSelectionToSiteKey(d);
    if (!bookableSet.has(key)) return;
    setDate(d);
    setStep(3);
  };

  const todaySiteKey = siteTodayKey();

  const sessionInfo = existingPackageSessionInfo(bookingMode);

  const steps = [
    m.booking_step_lesson(),
    m.booking_step_date(),
    m.booking_step_time(),
    m.booking_step_details(),
  ] as const;

  return (
    <>
      <section className="bg-ink page-band-pad text-foreground">
        <div className="container-page">
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            {m.booking_eyebrow()}
          </span>
          <h1 className="mt-2 font-display text-4xl font-bold md:text-5xl">{m.booking_title()}</h1>
        </div>
      </section>

      <section className="container-page page-content-pad">
        <ol className="mb-10 flex flex-wrap items-center gap-4 text-sm">
          {steps.map((s, i) => {
            const num = (i + 1) as 1 | 2 | 3 | 4;
            const done = step > num;
            const current = step === num;
            return (
              <li key={s} className="flex items-center gap-2">
                <span
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold",
                    done
                      ? "bg-accent text-accent-foreground"
                      : current
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground",
                  )}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : num}
                </span>
                <span
                  className={cn(
                    "font-medium",
                    current ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {s}
                </span>
                {i < 3 && <span className="text-border">·</span>}
              </li>
            );
          })}
        </ol>

        {step === 1 && (
          <div className="space-y-10">
            {offeringsLoading && (
              <div className="grid min-h-[24rem] gap-4 md:grid-cols-2 lg:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-3 rounded-xl border border-border bg-card p-6">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-8 w-24" />
                  </div>
                ))}
              </div>
            )}

            {!offeringsLoading && authed && myPackages.length > 0 && (
              <div>
                <h2 className="font-display text-2xl font-bold">{m.booking_your_packages()}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{m.booking_packages_hint()}</p>
                <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {myPackages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => selectExistingPackage(pkg)}
                      className={cn(
                        "rounded-xl border p-6 text-start transition-all hover:border-accent hover:shadow-md",
                        bookingMode?.kind === "existing-package" &&
                          bookingMode.memberPackage.id === pkg.id
                          ? "border-accent ring-2 ring-accent/30"
                          : "border-border bg-card",
                      )}
                    >
                      <div className="text-xs font-semibold uppercase tracking-wider text-accent">
                        {m.bookings_sessions_left({
                          remaining: pkg.sessions_remaining,
                          total: pkg.sessions_total,
                          minutes: pkg.duration_minutes,
                        })}
                      </div>
                      <div className="mt-1 font-display text-lg font-semibold">
                        {localizePriceLabel(pkg.label)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!offeringsLoading && (
              <>
                <div>
                  <h2 className="font-display text-2xl font-bold">{m.booking_individual()}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {m.booking_prices_cash_card()}
                  </p>
                  <div className="mt-6 grid gap-4 md:grid-cols-2">
                    {singlePrices.map((sp) => (
                      <BookingSitePriceCard
                        key={sp.id}
                        sitePrice={sp}
                        selected={
                          bookingMode?.kind === "single" && bookingMode.sitePrice.id === sp.id
                        }
                        onSelect={() => selectSitePrice(sp, "single")}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-display text-2xl font-bold">{m.booking_new_packages()}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {m.booking_new_packages_hint()}
                  </p>
                  <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {packagePrices.map((sp) => (
                      <BookingSitePriceCard
                        key={sp.id}
                        sitePrice={sp}
                        selected={
                          bookingMode?.kind === "new-package" && bookingMode.sitePrice.id === sp.id
                        }
                        onSelect={() => selectSitePrice(sp, "new-package")}
                        isPackage
                      />
                    ))}
                  </div>
                </div>

                {specialtyLessons.length > 0 && (
                  <div>
                    <h2 className="font-display text-2xl font-bold">{m.booking_specialty()}</h2>
                    <div className="mt-6 grid gap-4 md:grid-cols-2">
                      {specialtyLessons.map((l) => (
                        <button
                          key={l.id}
                          onClick={() => selectSpecialty(l)}
                          className={cn(
                            "rounded-xl border p-6 text-start transition-all hover:border-accent hover:shadow-md",
                            lessonType?.id === l.id && bookingMode?.kind === "specialty"
                              ? "border-accent ring-2 ring-accent/30"
                              : "border-border bg-card",
                          )}
                        >
                          <div className="text-xs font-semibold uppercase tracking-wider text-accent">
                            {l.sport}
                          </div>
                          <div className="mt-1 font-display text-lg font-semibold">{l.name}</div>
                          <p className="mt-1 text-sm text-muted-foreground">{l.description}</p>
                          <div className="mt-3 font-display text-2xl font-bold">
                            ${(l.price_cents / 100).toFixed(0)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  {m.booking_prefer_call()}{" "}
                  <a
                    href={SITE_PHONE_TEL}
                    className="text-accent underline underline-offset-2 hover:underline"
                  >
                    {SITE_PHONE}
                  </a>
                </p>
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setStep(1);
                setDate(undefined);
              }}
              className="mb-4"
            >
              <ChevronLeft className="me-1 h-4 w-4 rtl:rotate-180" /> {m.common_back()}
            </Button>
            <h2 className="font-display text-2xl font-bold">{m.booking_pick_date()}</h2>
            {cutoffQ.data && (
              <p className="mt-2 text-sm text-muted-foreground">
                {m.booking_cutoff({
                  cutoffLabel: formatSiteCutoffTime(cutoffQ.data.cutoffTime, locale),
                  timezone: m.booking_timezone(),
                })}
              </p>
            )}
            {bookableDatesQ.isLoading && (
              <p className="mt-2 text-sm text-muted-foreground">{m.booking_loading_dates()}</p>
            )}
            <div className="mt-6 inline-block rounded-xl border border-border bg-card p-4">
              <Calendar
                {...calendarI18n}
                mode="single"
                selected={date}
                month={calendarMonth}
                onMonthChange={setCalendarMonth}
                onSelect={handleDateSelect}
                disabled={(d) =>
                  calendarSelectionToSiteKey(d) < todaySiteKey ||
                  !bookableSet.has(calendarSelectionToSiteKey(d))
                }
                className={cn("p-3 pointer-events-auto")}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <Button variant="ghost" size="sm" onClick={() => setStep(2)} className="mb-4">
              <ChevronLeft className="me-1 h-4 w-4 rtl:rotate-180" /> {m.common_back()}
            </Button>
            <h2 className="font-display text-2xl font-bold">{m.booking_pick_time()}</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {date && formatSiteDateLabel(calendarSelectionToSiteKey(date), locale)}
            </p>
            {slotsQ.isLoading && (
              <div className="mt-6 grid min-h-[12rem] grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full rounded-lg" />
                ))}
              </div>
            )}
            {slotsQ.data && slotsQ.data.length === 0 && (
              <p className="mt-6 text-muted-foreground">{m.booking_no_openings()}</p>
            )}
            {!slotsQ.isLoading && (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {(slotsQ.data ?? []).map((s) => (
                  <button
                    key={s.start}
                    onClick={() => {
                      setSlot(s);
                      setStep(4);
                    }}
                    className="rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium transition-colors hover:border-accent hover:bg-accent/5"
                  >
                    {formatSiteTimeRange(s.start, s.end, locale)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="max-w-xl">
            <Button variant="ghost" size="sm" onClick={() => setStep(3)} className="mb-4">
              <ChevronLeft className="me-1 h-4 w-4 rtl:rotate-180" /> {m.common_back()}
            </Button>
            <h2 className="font-display text-2xl font-bold">{m.booking_your_details()}</h2>

            <div className="mt-4 rounded-lg border border-border bg-card p-4 text-sm">
              <div className="font-semibold">
                {bookingMode?.kind === "specialty"
                  ? (offeringLabel ?? lessonType?.name)
                  : offeringLabel
                    ? localizePriceLabel(offeringLabel)
                    : lessonType?.name}
              </div>
              {bookingMode?.kind === "single" || bookingMode?.kind === "new-package" ? (
                <div className="mt-1 font-display text-lg text-accent">
                  {formatDualPrice(
                    bookingMode.sitePrice.cash_cents,
                    bookingMode.sitePrice.card_cents,
                  )}
                  {bookingMode.kind === "new-package" && bookingMode.sitePrice.lesson_count && (
                    <span className="ms-2 text-sm font-normal text-muted-foreground">
                      {m.booking_lessons_count({ count: bookingMode.sitePrice.lesson_count })}
                    </span>
                  )}
                </div>
              ) : null}
              {bookingMode?.kind === "existing-package" && sessionInfo && (
                <p className="mt-2 text-muted-foreground">
                  {m.booking_session_of({
                    next: sessionInfo.nextSession,
                    total: sessionInfo.total,
                  })}
                  {sessionInfo.remainingAfter > 0
                    ? ` ${m.booking_remaining_after({ n: sessionInfo.remainingAfter })}`
                    : ` ${m.booking_last_session()}`}
                </p>
              )}
              {bookingMode?.kind === "new-package" &&
                bookingMode.sitePrice.lesson_count != null && (
                  <p className="mt-2 text-muted-foreground">
                    {m.booking_starting_package({ count: bookingMode.sitePrice.lesson_count })}
                  </p>
                )}
              <div className="mt-2 text-muted-foreground">
                {date &&
                  slot &&
                  `${formatSiteDateLabel(calendarSelectionToSiteKey(date), locale)} · ${formatSiteTimeRange(slot.start, slot.end, locale)}`}
              </div>
            </div>

            {!authed ? (
              <AuthInline heading={m.booking_auth_heading()} description={m.booking_auth_desc()} />
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  bookMut.mutate();
                }}
                className="mt-6 space-y-4"
              >
                <div>
                  <Label htmlFor="em">{m.auth_label_email()}</Label>
                  <Input id="em" type="email" value={userEmail} readOnly className="bg-muted/50" />
                </div>
                <div>
                  <Label htmlFor="sn">{m.booking_label_student()}</Label>
                  <Input
                    id="sn"
                    value={details.studentName}
                    onChange={(e) => setDetails({ ...details, studentName: e.target.value })}
                    required
                    maxLength={100}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sa">{m.booking_label_age()}</Label>
                    <Input
                      id="sa"
                      type="number"
                      min={3}
                      max={120}
                      value={details.studentAge}
                      onChange={(e) => setDetails({ ...details, studentAge: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="ph">{m.booking_label_phone()}</Label>
                    <Input
                      id="ph"
                      type="tel"
                      value={details.phone}
                      onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                      required
                      maxLength={30}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="no">{m.booking_label_notes()}</Label>
                  <Textarea
                    id="no"
                    rows={3}
                    value={details.notes}
                    onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                    maxLength={1000}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={bookMut.isPending}
                  className="w-full bg-accent text-accent-foreground hover:bg-ember-hot"
                >
                  {bookMut.isPending ? m.booking_booking() : m.booking_confirm()}
                </Button>
              </form>
            )}
          </div>
        )}
      </section>
    </>
  );
}
